/**
 * Hook for managing the template fill dialog flow.
 * Handles variable parsing, dialog state, substitution, and auto-send signaling.
 */

import { useCallback, useMemo, useState } from 'react'
import type { PromptTemplate } from '@/components/chat/prompt-template.interface'
import type { ParsedTemplateVariable } from '@/components/organisms/template-variable-editor/template-variable.interface'
import { parseTemplateVariables } from '@/components/organisms/template-variable-editor/template-variable-parser.util'
import type { TemplateVariableFormValues } from '@/components/organisms/template-variable-form/template-variable-form.interface'
import { substituteTemplateVariables } from '@/components/organisms/template-variable-form/template-variable-substitutor.util'

export interface UseChatTemplateFillReturn {
  selectedTemplate: PromptTemplate | null
  isTemplateFillOpen: boolean
  templateFillVariableList: ParsedTemplateVariable[]
  pendingTemplateSend: boolean
  openTemplateFill: (template: PromptTemplate) => void
  handleTemplateFillSubmit: (values: TemplateVariableFormValues) => void
  handleTemplateFillCancel: () => void
  clearPendingTemplateSend: () => void
  triggerPendingTemplateSend: () => void
}

interface UseChatTemplateFillParams {
  setInputValue: (value: string) => void
}

const useTemplateFillState = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<PromptTemplate | null>(null)
  const [isTemplateFillOpen, setIsTemplateFillOpen] = useState(false)
  return {
    selectedTemplate,
    setSelectedTemplate,
    isTemplateFillOpen,
    setIsTemplateFillOpen,
  }
}

const usePendingSend = () => {
  const [pendingTemplateSend, setPendingTemplateSend] = useState(false)
  const clearPendingTemplateSend = useCallback(() => setPendingTemplateSend(false), [])
  const triggerPendingTemplateSend = useCallback(() => setPendingTemplateSend(true), [])
  return {
    pendingTemplateSend,
    setPendingTemplateSend,
    clearPendingTemplateSend,
    triggerPendingTemplateSend,
  }
}

type FillState = ReturnType<typeof useTemplateFillState>
type PendingSend = ReturnType<typeof usePendingSend>

const useFillHandlers = (
  fillState: FillState,
  pendingSend: PendingSend,
  setInputValue: (value: string) => void
) => {
  const openTemplateFill = useCallback(
    (template: PromptTemplate) => {
      fillState.setSelectedTemplate(template)
      fillState.setIsTemplateFillOpen(true)
    },
    [fillState]
  )
  const handleTemplateFillSubmit = useCallback(
    (values: TemplateVariableFormValues) => {
      if (!fillState.selectedTemplate) return
      const substituted = substituteTemplateVariables(fillState.selectedTemplate.content, values)
      setInputValue(substituted)
      fillState.setIsTemplateFillOpen(false)
      fillState.setSelectedTemplate(null)
      pendingSend.setPendingTemplateSend(true)
    },
    [fillState, setInputValue, pendingSend]
  )
  const handleTemplateFillCancel = useCallback(() => {
    fillState.setIsTemplateFillOpen(false)
    fillState.setSelectedTemplate(null)
  }, [fillState])
  return {
    openTemplateFill,
    handleTemplateFillSubmit,
    handleTemplateFillCancel,
  }
}

export const useChatTemplateFill = ({
  setInputValue,
}: UseChatTemplateFillParams): UseChatTemplateFillReturn => {
  const fillState = useTemplateFillState()
  const pendingSend = usePendingSend()
  const handlers = useFillHandlers(fillState, pendingSend, setInputValue)

  const templateFillVariableList = useMemo(
    () =>
      fillState.selectedTemplate ? parseTemplateVariables(fillState.selectedTemplate.content) : [],
    [fillState.selectedTemplate]
  )

  return {
    selectedTemplate: fillState.selectedTemplate,
    isTemplateFillOpen: fillState.isTemplateFillOpen,
    templateFillVariableList,
    pendingTemplateSend: pendingSend.pendingTemplateSend,
    clearPendingTemplateSend: pendingSend.clearPendingTemplateSend,
    triggerPendingTemplateSend: pendingSend.triggerPendingTemplateSend,
    ...handlers,
  }
}
